<template>
    <div class="form-group">
        <label v-if="field_name" class="control-label" :for="field">
            <span>{{ translatedFieldName }}</span>
            <span
                v-if="isRequiredField"
                class="required-field-indicator"
                :title="requiredHelpText"
                :aria-label="requiredHelpText"
                tabindex="0"
            >
                *
            </span>
        </label>

        <input
            v-if="type !== 'select' && type !== 'multi-select'"
            :id="field"
            :type="type"
            v-model="inputValue"
            :placeholder="translatedPlaceholder"
            class="form-control"
            @blur="checkValue"
            @input="checkValue"
            :required="isRequiredField"
            :aria-describedby="addonAria"
            :aria-invalid="errorMessage"
            :readonly="type === 'readonly'"
        />
        <!-- 統一使用 vue-multiselect 處理 select 和 multi-select -->
        <div
            v-if="type === 'select' || type === 'multi-select'"
            style="display: flex; gap: 8px; align-items: flex-start"
        >
            <div style="flex: 1">
                <multiselect
                    v-model="internalValue"
                    :options="filteredOptions"
                    :multiple="type === 'multi-select'"
                    :close-on-select="type === 'select'"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :placeholder="
                        isLoading
                            ? $t('users.users_msg__loading')
                            : translatedPlaceholder
                    "
                    :label="optionLabel"
                    :track-by="optionTrackBy"
                    :searchable="searchable === 'true'"
                    :disabled="addingOption || isLoading"
                    :loading="isLoading"
                    :required="isRequiredField"
                    :allow-empty="!isRequiredField"
                    :class="{
                        'is-invalid':
                            errorMessage && isRequiredField && isValueEmpty,
                    }"
                    @search-change="onMultiSelectSearch"
                    @close="checkValue"
                    @select="checkValue"
                    @remove="checkValue"
                >
                    <template #noResult>
                        <span>{{ $t('users.users_msg__no_result') }}</span>
                    </template>
                </multiselect>
            </div>
            <button
                v-if="add_option_url"
                type="button"
                class="btn btn-outline-primary"
                @click="showAddModal = true"
                style="height: 38px"
            >
                +
            </button>
        </div>

        <!-- 新增選項 Modal -->
        <div v-if="showAddModal" class="modal-mask">
            <div class="modal-wrapper">
                <div class="modal-container">
                    <div class="modal-header">
                        <h5>
                            {{
                                $t('users.shops_msg__add') +
                                ' ' +
                                translatedFieldName
                            }}
                        </h5>
                    </div>
                    <div class="modal-body">
                        <input
                            v-model="newOptionName"
                            class="form-control"
                            :placeholder="translatedFieldName"
                        />
                    </div>
                    <div class="modal-footer">
                        <button
                            class="btn btn-secondary"
                            @click="closeAddModal"
                        >
                            {{ $t('users.users_msg__cancel') }}
                        </button>
                        <button
                            class="btn btn-primary"
                            @click="handleAddOption"
                            :disabled="addingOption"
                        >
                            {{ $t('users.users_msg__user_form_submit') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <small v-if="errorMessage" class="text-danger">{{
            errorMessage
        }}</small>
        <small v-if="successMessage" class="text-success">{{
            successMessage
        }}</small>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { checkInput, getOptions } from '@/api/users';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import apiClient from '@/api';

const REQUIRED_FIELD_HINT_KEY = 'users.users_msg__required_field_hint';
const REQUIRED_FIELD_HINT_FALLBACK = 'Required field';

export default {
    name: 'UserFormGroupInput',
    components: { Multiselect },
    props: {
        field: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            default: 'text',
        },
        // Vue 3 v-model 使用 modelValue prop
        modelValue: {
            type: [String, Number, Array],
            default: undefined,
        },
        // 保留 value prop 以支援 v-bind 展開的情況
        value: {
            type: [String, Number, Array],
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        field_name: {
            type: String,
            default: '',
        },
        check_url: {
            type: String,
            default: '',
        },
        required: {
            type: [String, Boolean],
            default: 'false',
        },
        options: {
            type: Array,
            default: () => [],
        },
        options_url: {
            //如果需要從API取得選項的內容
            type: String,
            default: '',
        },
        option_title: {
            type: String,
            default: '',
        },
        searchable: {
            type: String,
            default: 'false',
        },
        add_option_url: {
            type: String,
            default: '',
        },
        addOptionParams: {
            // 改為 camelCase
            type: Object,
            default: () => ({}),
        },
        multi_select_label: {
            type: String,
            default: 'name',
        },
        multi_select_track_by: {
            type: String,
            default: 'id',
        },
    },
    setup(props, { emit }) {
        const { t } = useI18n();

        // 計算實際要使用的值：優先使用 modelValue（v-model 綁定），否則使用 value（v-bind 展開）
        // 這解決了 v-bind="inputGroup" 和 v-model="formData[field]" 同時使用時的衝突
        const effectiveValue = computed(() => {
            // 明確處理 null 值的情況
            if (props.modelValue !== undefined && props.modelValue !== null) {
                return props.modelValue;
            }
            return props.value ?? ''; // 確保永遠不會返回 null
        });

        // 抽取值轉換邏輯為獨立函數，提高可重用性和可測試性
        const convertMultiSelectValue = newValue => {
            if (Array.isArray(newValue)) {
                return newValue.map(item => item.id || item);
            }
            return [];
        };

        const convertSelectValue = newValue => {
            if (newValue && typeof newValue === 'object' && newValue.id) {
                return newValue.id;
            }
            if (newValue && (newValue.username || newValue.name)) {
                // 處理沒有 id 但有 username 或 name 的情況
                return newValue.username || newValue.name || newValue;
            }
            if (typeof newValue === 'string' || typeof newValue === 'number') {
                return newValue;
            }
            return '';
        };

        const localValue = ref(effectiveValue.value);
        const errorMessage = ref('');
        const successMessage = ref('');
        const selectOptions = ref([...props.options]);
        const selectSearchText = ref('');
        const showAddModal = ref(false);
        const newOptionName = ref('');
        const addingOption = ref(false);
        const isOptionsLoaded = ref(false);

        // internalValue 用於 vue-multiselect 的 v-model（object 或 object array）
        // localValue 用於與父組件溝通（id 或 id array）
        const internalValue = ref(null);

        // 檢查值是否為空
        const isRequiredField = computed(() => {
            return props.required === true || props.required === 'true';
        });

        const requiredHelpText = computed(() => {
            const message = t(REQUIRED_FIELD_HINT_KEY);
            return message === REQUIRED_FIELD_HINT_KEY
                ? REQUIRED_FIELD_HINT_FALLBACK
                : message;
        });

        const isValueEmpty = computed(() => {
            const valueToCheck =
                props.type === 'select' || props.type === 'multi-select'
                    ? internalValue.value
                    : localValue.value;

            if (props.type === 'multi-select') {
                return (
                    !Array.isArray(internalValue.value) ||
                    internalValue.value.length === 0
                );
            }

            if (props.type === 'select') {
                return valueToCheck === null || valueToCheck === undefined;
            }

            if (Array.isArray(valueToCheck)) {
                return valueToCheck.length === 0;
            }

            if (typeof valueToCheck === 'string') {
                return valueToCheck.trim() === '';
            }

            return (
                valueToCheck === null ||
                valueToCheck === undefined ||
                valueToCheck === ''
            );
        });

        // 檢查是否正在載入選項
        // 只有在需要從 API 載入選項時才顯示 loading（有 options_url 且沒有固定選項）
        const isLoading = computed(() => {
            // 如果有固定選項，不需要 loading
            if (Array.isArray(props.options) && props.options.length > 0) {
                return false;
            }
            // 只有在有 options_url 且尚未載入完成時才顯示 loading
            return (
                (props.type === 'select' || props.type === 'multi-select') &&
                props.options_url &&
                !isOptionsLoaded.value
            );
        });

        const translatedFieldName = computed(() => {
            return t(`users.${props.field_name}`);
        });

        const translatedPlaceholder = computed(() => {
            // if placeholder is provided, use it; otherwise use field_name
            if (props.placeholder && props.placeholder !== '') {
                return t(`users.${props.placeholder}`);
            }
            return t(`users.${props.field_name}`);
        });

        const translatedOptionTitle = computed(() => {
            return t(`users.${props.option_title}`);
        });

        const addonAria = computed(() => {
            return `${props.field}-addon`;
        });

        // 將 string array 轉換為統一的 object array 格式 { name: string, id: string }
        const normalizedOptions = computed(() => {
            if (!Array.isArray(selectOptions.value)) return [];
            const getAppearanceModeLabel = value => {
                if (props.field !== 'appearance_mode') return value;
                return t('users.users_opt__appearance_mode_' + value);
            };
            return selectOptions.value.map(option => {
                if (typeof option === 'string') {
                    return { name: getAppearanceModeLabel(option), id: option };
                }
                if (
                    props.field === 'appearance_mode' &&
                    option &&
                    (option.id || option.name)
                ) {
                    const value = option.id || option.name;
                    return {
                        ...option,
                        name: getAppearanceModeLabel(value),
                    };
                }
                return option;
            });
        });

        const filteredOptions = computed(() => {
            if (!Array.isArray(normalizedOptions.value)) return [];
            if (!selectSearchText.value) return normalizedOptions.value;
            const searchText = selectSearchText.value.toLowerCase();
            const filtered = normalizedOptions.value.filter(option => {
                if (option && option.name) {
                    return option.name.toLowerCase().includes(searchText);
                }
                return false;
            });
            return Array.isArray(filtered) ? filtered : [];
        });

        const fetchOptionsByAPI = async props => {
            try {
                if (!props.options_url) {
                    // 沒有 options_url，不做任何事情（保留現有的 props.options）
                    isOptionsLoaded.value = true;
                    return;
                }
                isOptionsLoaded.value = false;
                const response = await getOptions(props.options_url);
                selectOptions.value = response;
                isOptionsLoaded.value = true;
                // Emit event to parent with the field name and the fetched options
                emit('options-loaded', {
                    field: props.field,
                    options: response,
                });
            } catch (error) {
                console.error(
                    'Error fetching options:',
                    error,
                    '; API url: ',
                    props.options_url
                );
                isOptionsLoaded.value = true; // 即使錯誤也標記為已載入，避免永久 loading
                selectOptions.value = [];
            }
        };

        const fetchOptions = async () => {
            if (Array.isArray(props.options) && props.options.length > 0) {
                // 有固定選項，直接使用 props.options
                selectOptions.value = [...props.options];
                isOptionsLoaded.value = true;
            } else if (props.options_url) {
                // 沒有固定選項但有 options_url，從 API 取得
                fetchOptionsByAPI(props);
            } else {
                // 如果既沒有 options_url 也沒有 options，清空選項
                selectOptions.value = [];
                isOptionsLoaded.value = true;
            }
        };

        // Watch for changes in effectiveValue (which prioritizes modelValue prop over value prop)
        watch(
            effectiveValue,
            newValue => {
                // 只有當值真正不同時才更新，避免循環
                if (
                    JSON.stringify(newValue) !==
                    JSON.stringify(localValue.value)
                ) {
                    localValue.value = newValue;
                }
            },
            { immediate: true, deep: true } // 添加 immediate 和 deep 選項
        );

        // 對於非 select/multi-select 類型，直接 watch localValue 並 emit
        watch(
            localValue,
            newValue => {
                if (props.type !== 'select' && props.type !== 'multi-select') {
                    emit('update:modelValue', newValue);
                }
            },
            { immediate: true }
        );

        watch(
            () => props.options_url,
            (newUrl, oldUrl) => {
                if (
                    (props.type === 'multi-select' ||
                        props.type === 'select') &&
                    newUrl !== oldUrl &&
                    oldUrl !== undefined &&
                    oldUrl !== '' // 只有當 oldUrl 有實際值時才清空選擇（初始化時不清空）
                ) {
                    // 清空選擇
                    if (props.type === 'multi-select') {
                        internalValue.value = [];
                        localValue.value = [];
                        emit('update:modelValue', []);
                    } else {
                        internalValue.value = null;
                        localValue.value = '';
                        emit('update:modelValue', '');
                    }
                }
                // 當 options_url 變化時重新拉取選項
                fetchOptions();
            },
            { immediate: true }
        );

        // 監聽 props.options 變化
        watch(
            () => props.options,
            newOptions => {
                if (Array.isArray(newOptions)) {
                    // 修復：總是響應 props.options 的變化，包括空數組
                    fetchOptions();
                    isOptionsLoaded.value = true;
                }
            },
            { immediate: true, deep: true }
        );

        // Emit an event when the internalValue changes
        // 只針對 select/multi-select 類型處理
        watch(
            internalValue,
            newValue => {
                // 只有 select 和 multi-select 才使用 internalValue
                if (props.type !== 'select' && props.type !== 'multi-select') {
                    return;
                }

                if (props.type === 'multi-select') {
                    const idArray = convertMultiSelectValue(newValue);
                    localValue.value = idArray;
                    emit('update:modelValue', idArray);
                } else if (props.type === 'select') {
                    const value = convertSelectValue(newValue);
                    localValue.value = value;
                    emit('update:modelValue', value);
                } else {
                    // 其他類型直接 emit
                    emit('update:modelValue', newValue);
                }
            },
            { deep: true }
        );

        // 修正：處理 select 和 multi-select 的預設值轉換（id -> object）
        watch(
            [
                () => isOptionsLoaded.value,
                () => props.value,
                () => selectOptions.value,
            ],
            ([loaded, value, options]) => {
                if (
                    !loaded ||
                    !Array.isArray(options) ||
                    options.length === 0
                ) {
                    return;
                }

                // 處理 multi-select：id array -> object array
                if (props.type === 'multi-select') {
                    // 使用 normalizedOptions 來查找（處理 string options 的情況）
                    const normalizedOpts = normalizedOptions.value;
                    if (
                        Array.isArray(value) &&
                        value.length > 0 &&
                        (typeof value[0] === 'number' ||
                            typeof value[0] === 'string')
                    ) {
                        const objectArray = value
                            .map(id => {
                                const option = normalizedOpts.find(
                                    opt => opt.id === id || opt.name === id
                                );
                                if (!option) {
                                    console.warn(
                                        `Option with id ${id} not found in selectOptions for field ${props.field}`
                                    );
                                }
                                return (
                                    option || {
                                        id,
                                        name: `Unknown Option ${id}`,
                                    }
                                );
                            })
                            .filter(option => option);

                        if (
                            JSON.stringify(internalValue.value) !==
                            JSON.stringify(objectArray)
                        ) {
                            internalValue.value = objectArray;
                        }
                    } else if (
                        Array.isArray(value) &&
                        value.length > 0 &&
                        typeof value[0] === 'object'
                    ) {
                        const validatedArray = value
                            .map(item => {
                                if (item && (item.id || item.name)) {
                                    const option = normalizedOpts.find(
                                        opt =>
                                            opt.id === item.id ||
                                            opt.name === item.name
                                    );
                                    return option || item;
                                }
                                return item;
                            })
                            .filter(item => item);

                        if (
                            JSON.stringify(internalValue.value) !==
                            JSON.stringify(validatedArray)
                        ) {
                            internalValue.value = validatedArray;
                        }
                    } else if (Array.isArray(value) && value.length === 0) {
                        internalValue.value = [];
                    }
                }

                // 處理 select：id/string -> object
                if (props.type === 'select') {
                    if (
                        typeof value === 'number' ||
                        (typeof value === 'string' && value !== '')
                    ) {
                        // 使用 normalizedOptions 來查找（string 已經被轉換為 object）
                        const normalizedOpts = normalizedOptions.value;
                        const option = normalizedOpts.find(
                            opt =>
                                opt.id === value ||
                                opt.id === Number(value) ||
                                opt.username === value ||
                                opt.name === value
                        );
                        if (
                            option &&
                            JSON.stringify(internalValue.value) !==
                                JSON.stringify(option)
                        ) {
                            internalValue.value = option;
                        }
                    } else if (
                        value === '' ||
                        value === null ||
                        value === undefined
                    ) {
                        internalValue.value = null;
                    }
                }
            },
            { immediate: true, deep: true }
        );

        // 當 options 載入完成後，重新處理 value
        watch(
            () => selectOptions.value,
            newOptions => {
                if (
                    (props.type === 'multi-select' ||
                        props.type === 'select') &&
                    Array.isArray(newOptions) &&
                    newOptions.length > 0
                ) {
                    isOptionsLoaded.value = true;
                }
            },
            { immediate: true, deep: true }
        );

        const checkValue = async () => {
            // 先做 required 驗證（所有欄位通用）
            if (isRequiredField.value && isValueEmpty.value) {
                errorMessage.value = t(
                    `users.users_msg__${props.field}_is_required`
                );
                successMessage.value = '';
                emit('valid', false);
                return;
            }

            if (props.type === 'select' || props.type === 'multi-select') {
                errorMessage.value = '';
                successMessage.value = '';
                emit('valid', true);
                return;
            }

            if (props.type === 'readonly') {
                return;
            }
            if (props.check_url) {
                try {
                    const response = await checkInput(
                        props.check_url,
                        localValue.value
                    );
                    if (response.error) {
                        errorMessage.value = t('users.' + response.error);
                        successMessage.value = '';
                        emit('valid', false);
                    } else {
                        successMessage.value = t('users.' + response.message);
                        errorMessage.value = '';
                        emit('valid', true);
                    }
                } catch (error) {
                    if (error.response.status === 400) {
                        errorMessage.value = t(
                            'users.' + error.response.data.error
                        );
                        successMessage.value = '';
                        emit('valid', false);
                    }
                }
            } else {
                // 如果沒有提供 check_url，則不進行檢查
                errorMessage.value = '';
                successMessage.value = '';
                emit('valid', true);
            }
        };

        const closeAddModal = () => {
            showAddModal.value = false;
            newOptionName.value = '';
            addingOption.value = false; // 關閉 modal 時一定恢復 enabled
        };

        const handleAddOption = async () => {
            if (!props.add_option_url || !newOptionName.value) return;
            addingOption.value = true;
            try {
                // 合併基本資料和額外參數
                const requestData = {
                    name: newOptionName.value,
                    ...props.addOptionParams,
                };

                const res = await apiClient.post(
                    props.add_option_url,
                    requestData
                );

                // 重新從 API 載入選項列表，以確保包含新增的選項
                if (props.options_url) {
                    await fetchOptionsByAPI(props);
                } else {
                    // 如果沒有 options_url，才手動新增到選項列表
                    selectOptions.value.push(res.data);
                }

                // 根據類型設置不同的 internalValue
                if (props.type === 'multi-select') {
                    // 對於 multi-select，添加到現有數組中
                    if (!Array.isArray(internalValue.value)) {
                        internalValue.value = [];
                    }
                    // 添加新選項對象到 internalValue
                    internalValue.value.push(res.data);
                    await checkValue();
                } else if (props.type === 'select') {
                    // 對於單選，設置整個 object
                    internalValue.value = res.data;
                    await checkValue();
                }

                emit('option-added', res.data);
                closeAddModal();
            } catch (e) {
                // 可根據實際API回傳顯示錯誤
                alert(e.response?.data?.message || '新增失敗');
            } finally {
                addingOption.value = false;
            }
        };

        const optionLabel = props.multi_select_label || 'name';
        const optionTrackBy = props.multi_select_track_by || 'id';

        // 多選搜尋
        const onMultiSelectSearch = query => {
            selectSearchText.value = query;
        };

        return {
            // 提供給模板使用的 v-model 綁定值
            inputValue: localValue,
            internalValue,
            isRequiredField,
            requiredHelpText,
            isValueEmpty,
            isLoading,
            translatedFieldName,
            translatedPlaceholder,
            translatedOptionTitle,
            checkValue,
            errorMessage,
            successMessage,
            addonAria,
            selectOptions,
            filteredOptions,
            selectSearchText,
            fetchOptions,
            showAddModal,
            newOptionName,
            addingOption,
            closeAddModal,
            handleAddOption,
            optionLabel,
            optionTrackBy,
            onMultiSelectSearch,
        };
    },
};
</script>

<style>
.required-field-indicator {
    color: #e3342f;
    font-size: 0.85em;
    font-weight: 400;
    margin-left: 4px;
    white-space: nowrap;
    cursor: help;
}
.text-danger {
    color: #e3342f;
}
.text-success {
    color: #38c172;
}
.modal-mask {
    position: fixed;
    z-index: 9998;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
}
.modal-wrapper {
    width: 100%;
    max-width: 400px;
}
.modal-container {
    background: var(--ca-color-surface, #fff);
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.modal-header {
    margin-bottom: 10px;
}
.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 10px;
}
</style>
